<template>
  <div class="data-hub-viewer">
    <div v-if="isPresentMoment">
      <vs-divider
        >Data received from device on
        {{ buoyData.length > 0 ? parseDate(buoyData[0].date) : "-" }}
        -</vs-divider
      >
      <div class="grid-results-buoys">
        <div v-for="(data, index) in buoyData" :key="index">
          <div class="header-item">
            {{ data.descripcion }}
          </div>
          <div class="result-item">
            <vs-chip>
              <vs-avatar text icon="nfc" class="avatar" />
              <span class="label-result"
                >{{ data.value.toFixed(2) }} {{ data.units }}</span
              >
            </vs-chip>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <section v-if="isPastMoment" class="query-section">
        <vs-divider position="left" class="viewer-divider">Dates</vs-divider>
        <datahub-viewer-filter
          :date-range="productDates"
          @filterClick="loadData"
        ></datahub-viewer-filter>
      </section>
      <section v-if="graph.serie.length > 0" class="graph-section">
        <line-thresholds-chart
          :z-data-serie="graph.serie"
          :z-variable-info="graph.variable"
          :z-data-source="graph.source"
          :z-data-threshold="graph.threshold"
          :z-subtitle="graph.subtitle"
          :z-title="graph.title"
        />
      </section>
    </div>
  </div>
</template>

<script>
import { getProductDateLimits, getProductDataSerie } from "@/api/thredds";
import DataHubViewerFilter from "@/components/DataHubViewerFilter";
import LineChart from "@/components/LineChart";
import { getBuoyLastMeasure } from "@/api/sensors";
import { MOMENTS } from "@/constants.js";
import { mapState, mapGetters, mapActions } from "vuex";
export default {
  components: {
    "datahub-viewer-filter": DataHubViewerFilter,
    "line-thresholds-chart": LineChart
  },
  data() {
    return {
      dates: {},
      graph: {
        serie: [],
        source: {},
        thresholds: {}, //{max:0,min:0}
        variable: {},
        title: "",
        subtitle: ""
      },
      buoyData: {},
      moments: MOMENTS
    };
  },
  computed: {
    ...mapState("map", {
      marker: state => state.marker
    }),
    ...mapState({
      activeProduct: state => state.activeProduct,
      activeDevice: state => state.activeDevice,
      btmDatahubActiveMoment: state => state.btmDatahubActiveMoment
    }),
    ...mapGetters("map", {
      roundedMarkerCoords: "roundedMarkerCoords"
    }),
    productDates() {
      return {
        ini: this.activeProduct.startDate,
        end: this.activeProduct.endDate
      };
    },
    isPastMoment() {
      return this.btmDatahubActiveMoment === this.moments.past;
    },
    isPresentMoment() {
      return this.btmDatahubActiveMoment === this.moments.present;
    }
  },
  created() {
    this.toggleLoading(true);
  },
  mounted() {
    this.toggleLoading(false);
    if (
      this.btmDatahubActiveMoment === this.moments.shortTerm ||
      this.btmDatahubActiveMoment === this.moments.climateChange
    ) {
      this.loadDataWithDynamicDates();
    } else if (this.btmDatahubActiveMoment === this.moments.present) {
      this.loadDeviceData();
    }
  },
  methods: {
    ...mapActions({
      toggleLoading: "toggleLoading"
    }),
    async loadDataWithDynamicDates() {
      const dates = await this.getDataDateRange();
      this.loadData(dates);
    },
    loadData(dates) {
      this.dates = dates;
      this.requestData();
    },
    async loadDeviceData() {
      if (this.activeDevice.isRadar) {
        console.info("Es Radar");
      } else {
        try {
          const lastBuoyMeasure = await getBuoyLastMeasure(
            this.activeDevice.id
          );

          this.buoyData = lastBuoyMeasure.dataCustom;
        } catch (error) {
          this.$_utilsMixin_notify("Error getting device last data", error, 2);
        }
      }
    },
    async requestData() {
      try {
        this.toggleLoading(true);
        const dataSerie = await this.getDataSerie();
        this.setupGraphData(dataSerie);
      } catch (error) {
        this.$_utilsMixin_notify("Error getting data serie", error, 2);
      } finally {
        this.toggleLoading(false);
      }
    },
    async getDataDateRange() {
      try {
        this.toggleLoading(true);
        const dateRange = await getProductDateLimits(
          this.activeProduct.urlBase,
          this.activeProduct.latest
        );
        return dateRange;
      } catch (error) {
        this.$_utilsMixin_notify("Error getting data dates", error, 2);
      } finally {
        this.toggleLoading(false);
      }
    },
    async getDataSerie() {
      /* Request product data in coords, for a variable between dates*/
      const params = this.getParams(this.activeProduct.variable);
      const data = await getProductDataSerie(params);
      return data;
    },
    setupGraphData(dataSerie) {
      const variableInfo = {
        alias: this.activeProduct.variable.alias,
        units: this.activeProduct.variable.units
      };
      const source = {
        name: this.activeProduct.source.alias,
        link: this.activeProduct.source.urlEntity
      };

      this.graph.title = `${this.$_utilsMixin_capitalize(
        variableInfo.alias
      )} at ${this.roundedMarkerCoords.lon}° - ${
        this.roundedMarkerCoords.lat
      }° (Lon-Lat)`;
      this.graph.subtitle = `(in ${variableInfo.units})`;
      this.graph.variable = variableInfo;
      this.graph.source = source;
      this.graph.serie = dataSerie;
    },
    getParams(variable) {
      return {
        baseUrl: this.activeProduct.urlBase,
        latestPath: this.activeProduct.latest,
        variable: variable,
        date: this.dates,
        point: this.marker
      };
    },
    parseDate(dateString) {
      const dateObj = new Date(dateString);
      return dateObj.toLocaleDateString("en-EN", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric"
      });
    }
  }
};
</script>
<style lang="scss" scoped>
.data-hub-viewer {
  flex-grow: 1;
  width: 100%;
}
.viewer-divider {
  padding-left: 1rem;
  padding-right: 1rem;
}
.grid-results-buoys {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: 20px 1fr;
  grid-gap: 1rem;
  margin-bottom: 2rem;
  margin-top: 2rem;
}
.header-item {
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.result-item {
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  justify-content: center;
}
.date-label {
  font-size: 0.8rem;
}
.label-result {
  font-size: 1rem;
}
.avatar {
  background: $secondary-color !important;
}
</style>
